// Picking files: a drop area that is also a button — the same one the staff
// app uses, so handing in homework feels like adding course material.

import { useId, useRef, useState } from 'react'
import type { CSSProperties, DragEvent as ReactDragEvent, ReactNode } from 'react'

import { useT } from '../i18n'
import common from '../i18n/common'
import { Icon } from './icons'

// ---------------------------------------------------------------- dropzone --

/** Whether a file matches an `accept` list: ".pdf", "image/*", "video/mp4". */
export function accepts(file: File, accept: string): boolean {
  const tokens = accept
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
  if (tokens.length === 0) return true
  const name = file.name.toLowerCase()
  const type = file.type.toLowerCase()
  return tokens.some((tok) => {
    if (tok.startsWith('.')) return name.endsWith(tok)
    if (tok.endsWith('/*')) return type.startsWith(tok.slice(0, -1))
    return type === tok
  })
}

/**
 * A drop area for files that is also a button: click, Enter or Space opens the
 * file picker, and dropping files on it does the same as picking them. Files
 * the `accept` list does not cover are refused here with their names, rather
 * than silently dropped or sent to a server that will refuse them anyway.
 */
export function Dropzone({
  accept,
  multiple,
  disabled,
  onFiles,
  title,
  hint,
  children,
  className,
  style,
}: {
  accept: string
  multiple?: boolean
  disabled?: boolean
  onFiles: (files: File[]) => void
  title: string
  hint?: string
  children?: ReactNode
  className?: string
  style?: CSSProperties
}) {
  const t = useT(common)
  const input = useRef<HTMLInputElement>(null)
  const hintId = useId()
  const [over, setOver] = useState(false)
  const [refused, setRefused] = useState<string[]>([])
  // dragenter and dragleave fire for every child crossed; counting them is the
  // only reliable way to know the pointer has really left.
  const depth = useRef(0)

  const take = (list: FileList | null) => {
    const all = Array.from(list ?? [])
    const ok = all.filter((f) => accepts(f, accept))
    setRefused(all.filter((f) => !accepts(f, accept)).map((f) => f.name))
    const picked = multiple ? ok : ok.slice(0, 1)
    if (picked.length > 0) onFiles(picked)
  }

  const open = () => {
    if (!disabled) input.current?.click()
  }

  const onDrag = (e: ReactDragEvent<HTMLDivElement>) => {
    if (!Array.from(e.dataTransfer.types).includes('Files')) return
    e.preventDefault()
    if (disabled) {
      e.dataTransfer.dropEffect = 'none'
      return
    }
    e.dataTransfer.dropEffect = 'copy'
    if (e.type === 'dragenter') {
      depth.current++
      setOver(true)
    } else if (e.type === 'dragleave') {
      depth.current = Math.max(0, depth.current - 1)
      if (depth.current === 0) setOver(false)
    }
  }

  const classes = ['dropzone', over && 'is-over', disabled && 'is-disabled', className].filter(Boolean).join(' ')

  return (
    <div
      className={classes}
      style={style}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-describedby={hint ? hintId : undefined}
      onClick={open}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          open()
        }
      }}
      onDragEnter={onDrag}
      onDragOver={onDrag}
      onDragLeave={onDrag}
      onDrop={(e) => {
        if (!Array.from(e.dataTransfer.types).includes('Files')) return
        e.preventDefault()
        depth.current = 0
        setOver(false)
        if (!disabled) take(e.dataTransfer.files)
      }}
    >
      <Icon name="upload" size={26} className="dropzone__icon" />
      <div className="dropzone__title">{over ? t('dropRelease') : title}</div>
      {hint && (
        <div className="dropzone__hint" id={hintId}>
          {hint}
        </div>
      )}
      {children}
      {refused.length > 0 && (
        <div className="dropzone__error" role="alert">
          {t('wrongType', { name: refused.join(', ') })}
        </div>
      )}
      <input
        ref={input}
        type="file"
        hidden
        accept={accept}
        multiple={multiple}
        tabIndex={-1}
        onChange={(e) => {
          take(e.target.files)
          // Picking the same file again must still fire a change.
          e.target.value = ''
        }}
      />
    </div>
  )
}
